<template>
  <section id="topics" class="section section-observed topic-section cms-layout-editorial">
    <p v-if="front.pageSubtitle" class="editorial-subtitle">{{ front.pageSubtitle }}</p>

    <article v-if="primaryBlock" class="editorial-main">
      <BlockRenderer :block="primaryBlock" />
    </article>

    <div v-if="extraBlocks.length" class="editorial-extras">
      <article v-for="block in extraBlocks" :key="block.blockId || block.blockCode" class="editorial-extra-card">
        <BlockRenderer :block="block" />
      </article>
    </div>

    <div v-if="isHistoryPage" class="history-panel">
      <div class="history-panel-head">
        <strong>沿革列表</strong>
        <span>共 {{ total }} 条</span>
      </div>

      <div v-if="loading" class="empty-panel">
        <p class="muted-copy">正在加载...</p>
      </div>
      <div v-else-if="historyItems.length" class="history-list">
        <article v-for="(entry, index) in historyItems" :key="entry.entryId" class="history-card">
          <button type="button" class="history-main" @click="openArticle(entry)">
            <div class="article-index" aria-label="序号">
              <strong>{{ itemIndex(index) }}</strong>
            </div>
            <div class="article-body">
              <div class="history-meta">
                <time>{{ formatDate(entry.publishTime) }}</time>
              </div>
              <h3>{{ entry.title }}</h3>
              <p class="history-category">学会沿革</p>
            </div>
          </button>
          <div class="history-actions">
            <button type="button" class="ghost-action compact" @click="openArticle(entry)">查看详情</button>
          </div>
        </article>
      </div>
      <div v-else class="empty-panel">
        <p class="muted-copy">暂无学会沿革文章，请在管理后台「学会沿革」中维护。</p>
      </div>

      <FrontPagination
        :page-num="pageNum"
        :page-size="pageSize"
        :total="total"
        @change="onPageChange"
      />
    </div>

    <CmsEntryDetailPanel v-if="front.selectedCmsEntry" />
  </section>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '../../../api/paleo'
import FrontPagination from '../../common/FrontPagination.vue'
import CmsEntryDetailPanel from '../CmsEntryDetailPanel.vue'
import BlockRenderer from '../blocks/BlockRenderer.vue'

export default {
  name: 'EditorialLayout',
  components: { CmsEntryDetailPanel, BlockRenderer, FrontPagination },
  inject: ['front'],
  data() {
    return {
      pageNum: 1,
      pageSize: 5,
      total: 0,
      historyItems: [],
      loading: false,
      loadSeq: 0
    }
  },
  computed: {
    isHistoryPage() {
      return this.front.page === 'history'
    },
    primaryBlock() {
      return this.front.activeBlocks[0] || null
    },
    extraBlocks() {
      return this.front.activeBlocks.slice(1)
    }
  },
  watch: {
    'front.page': {
      immediate: true,
      handler(val) {
        if (val === 'history') {
          this.pageNum = 1
          this.loadHistory()
        }
      }
    }
  },
  methods: {
    itemIndex(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    onPageChange(page) {
      this.pageNum = page
      this.loadHistory()
    },
    async loadHistory() {
      if (!this.isHistoryPage) return
      const seq = ++this.loadSeq
      this.loading = true
      try {
        const result = await listPublicCmsEntries({
          moduleCode: 'timeline',
          scope: 'society',
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        if (seq !== this.loadSeq) return
        const { rows, total } = unwrapPublicPage(result, {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        this.historyItems = rows.map(item => this.front.mapCmsEntry(item))
        this.total = total
      } catch (error) {
        if (seq !== this.loadSeq) return
        if (!this.historyItems.length) {
          this.historyItems = []
          this.total = 0
        }
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
    formatDate(value) {
      const raw = String(value || '').trim()
      if (!raw) return '待发布'
      return raw.slice(0, 10)
    },
    openArticle(entry) {
      if (this.front && typeof this.front.openCmsEntry === 'function') {
        this.front.openCmsEntry(entry)
      }
    }
  }
}
</script>

<style scoped>
.editorial-subtitle {
  margin: 0 0 24px;
  max-width: 960px;
  color: var(--subtle);
  font-size: 17px;
  line-height: 1.8;
}

.editorial-main {
  max-width: none;
  margin-bottom: 36px;
}

.editorial-extras {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}

.editorial-extra-card {
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  padding: 18px;
  background: #fff;
}

.history-panel {
  width: 100%;
  max-width: var(--content-max, 1100px);
  margin: 0 auto;
  padding: 24px 28px 28px;
  border: 1px solid var(--border);
  border-radius: var(--front-radius);
  background: #fff;
  box-shadow: var(--shadow-soft, 0 8px 24px rgba(0, 43, 73, 0.06));
}

.history-panel-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border);
}

.history-panel-head strong {
  color: var(--primary);
  font-size: 16px;
}

.history-panel-head span {
  color: var(--subtle);
  font-size: 14px;
}

.history-list {
  display: grid;
  gap: 14px;
}

.history-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 16px 18px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fbfdff;
}

.history-card:hover {
  border-color: rgba(26, 90, 147, 0.35);
  box-shadow: 0 14px 34px rgba(0, 29, 54, 0.08);
}

.history-main {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.article-index {
  display: grid;
  place-items: center;
  min-width: 68px;
  min-height: 68px;
  border-radius: 8px;
  background: linear-gradient(180deg, #eef6ff, #f8fbff);
  color: var(--primary);
}

.article-index strong {
  font-size: 26px;
  line-height: 1;
  font-weight: 800;
}

.article-body {
  min-width: 0;
}

.history-meta {
  margin-bottom: 8px;
  color: var(--subtle);
  font-size: 12px;
}

.history-card h3 {
  margin: 0;
  color: var(--ink, var(--primary));
  font-size: 18px;
  line-height: 1.4;
}

.history-category {
  margin: 8px 0 0;
  color: var(--subtle);
  font-size: 13px;
  line-height: 1.4;
}

.history-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
}

.muted-copy {
  margin: 0;
  color: var(--subtle);
  line-height: 1.7;
  text-align: center;
}

.empty-panel {
  padding: 40px 20px;
  text-align: center;
  border: 1px dashed var(--border);
  border-radius: var(--front-radius);
  background: #fff;
}

@media (max-width: 720px) {
  .history-panel {
    padding: 18px;
  }

  .history-card {
    grid-template-columns: 1fr;
  }

  .history-main {
    grid-template-columns: 1fr;
  }

  .article-index {
    display: inline-flex;
    justify-content: center;
    gap: 8px;
    width: fit-content;
    min-height: auto;
    padding: 8px 12px;
  }

  .history-actions {
    justify-content: flex-start;
  }
}
</style>
